/**
 * Symphony GROUP CHAT — read receipts, "Seen by", typing, Maestro thinking and online / last seen (design §21,
 * 2026-11-01). Pure: no React and no fetch, so the hooks (`lib/group-live-state.ts`, `lib/use-group-chat-session.ts`)
 * only move data. iOS `GroupReceipts.swift` and Android `GroupReceipts.kt` decide the same cases;
 * `group-receipts.test.ts` pins this one.
 *
 * People only: a Maestro never turns ticks blue and never appears in "Seen by" (it is never in the receipts).
 * Everything here is a HINT — a frame or an answer that does not parse is dropped, never an error.
 */
import { firstName, ownerShortName, shortName, type GroupParticipant, type GroupSummary, type MessageAuthor, type Translate } from './group-chat';

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);
const ms = (iso: string | null | undefined): number => (iso ? Date.parse(iso) : Number.NaN);

// ─── Receipts (the window's `groupReceipts`) ────────────────────────────────────────────────────────────────

/** One OTHER person of the group and how far they received and read it, as the viewer may see it. */
export interface GroupReceiptMember {
  metaSiteId: string;
  name: string;
  avatarUrl: string | null;
  readDate: string | null;
  readMessageId: string | null;
  deliveredDate: string | null;
  /** Their read receipts are off (or a block): only their deliveries count. */
  readsHidden: boolean;
  /** They joined with history from this time: a message before it is not theirs to receive. */
  historyFrom: string | null;
}
export interface GroupReceipts {
  /** The viewer's own read receipts switch: off, they see nobody's reads. */
  readsVisible: boolean;
  members: GroupReceiptMember[];
}

function parseReceiptMember(raw: unknown): GroupReceiptMember | null {
  if (!isObject(raw)) return null;
  const metaSiteId = str(raw.metaSiteId);
  if (!metaSiteId) return null;
  return {
    metaSiteId,
    name: typeof raw.name === 'string' ? raw.name : '',
    avatarUrl: str(raw.avatarUrl),
    readDate: str(raw.readDate),
    readMessageId: str(raw.readMessageId),
    deliveredDate: str(raw.deliveredDate),
    readsHidden: raw.readsHidden === true,
    historyFrom: str(raw.historyFrom),
  };
}

/** The window answer's `groupReceipts`; null when absent (a private chat, an older page, an older server). */
export function parseGroupReceipts(raw: unknown): GroupReceipts | null {
  if (!isObject(raw)) return null;
  const members = (Array.isArray(raw.members) ? raw.members : []).map(parseReceiptMember).filter((m): m is GroupReceiptMember => !!m);
  return { readsVisible: raw.readsVisible === true, members };
}

export type TickState = 'sent' | 'delivered' | 'read';

/** The later of two ISO dates (either may be null). */
function later(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return ms(b) > ms(a) ? b : a;
}

/**
 * The ticks on the viewer's OWN message created at `createdAt`: ✓ sent; ✓✓ every person it is for has it; blue ✓✓
 * every one read it — never while one hides their reads, nor while the viewer's own receipts are off. A person whose
 * `historyFrom` is after the message is not counted; alone in the group (nobody counted) it stays ✓.
 */
export function tickState(createdAt: string | null | undefined, receipts: GroupReceipts | null | undefined): TickState {
  if (!receipts) return 'sent';
  const created = ms(createdAt ?? null);
  if (!Number.isFinite(created)) return 'sent';
  const relevant = receipts.members.filter((m) => {
    const from = ms(m.historyFrom);
    return !Number.isFinite(from) || from <= created;
  });
  if (relevant.length === 0) return 'sent';
  const read = receipts.readsVisible && relevant.every((m) => !m.readsHidden && m.readDate != null && ms(m.readDate) >= created);
  if (read) return 'read';
  const delivered = relevant.every((m) => {
    const got = later(m.deliveredDate, m.readDate);
    return got != null && ms(got) >= created;
  });
  return delivered ? 'delivered' : 'sent';
}

/** A member whose read the viewer may see: their date, receipts on both sides. */
export function readVisible(receipts: GroupReceipts, m: GroupReceiptMember): boolean {
  return receipts.readsVisible && !m.readsHidden && m.readDate != null && Number.isFinite(ms(m.readDate));
}

export interface SeenByRow {
  id?: string;
  finalId?: string;
  createdDate?: string;
}

/**
 * "Seen by": each member with a visible read, anchored under the NEWEST loaded row created at or before their read.
 * `messages` is the visible transcript, chronological; a row without an id or a date (a pending post) anchors nobody.
 * Keyed by the row's id (`finalId ?? id`). The viewer is never listed (`viewerId`), and a read older than every
 * loaded row anchors nowhere.
 */
export function seenByAnchors(messages: readonly SeenByRow[], receipts: GroupReceipts | null | undefined, viewerId?: string | null): Map<string, GroupReceiptMember[]> {
  const anchors = new Map<string, GroupReceiptMember[]>();
  if (!receipts || !receipts.readsVisible) return anchors;
  const dated = messages
    .map((m) => ({ id: m.finalId ?? m.id, at: ms(m.createdDate ?? null) }))
    .filter((m): m is { id: string; at: number } => !!m.id && Number.isFinite(m.at));
  for (const member of receipts.members) {
    if (member.metaSiteId === viewerId || !readVisible(receipts, member)) continue;
    const readAt = ms(member.readDate);
    let anchor: string | null = null;
    for (let i = dated.length - 1; i >= 0; i--) {
      if (dated[i].at <= readAt) {
        anchor = dated[i].id;
        break;
      }
    }
    if (!anchor) continue;
    const list = anchors.get(anchor);
    if (list) list.push(member);
    else anchors.set(anchor, [member]);
  }
  return anchors;
}

/** The avatars a "Seen by" row draws (up to three) and its "+N". */
export function seenByStack(members: readonly GroupReceiptMember[], max = 3): { shown: GroupReceiptMember[]; plus: number } {
  return { shown: members.slice(0, max), plus: Math.max(0, members.length - max) };
}

/** "Seen by Caro, Alex" — first names, every one of them (an accessibility label, never truncated). */
export function seenByLabel(members: readonly GroupReceiptMember[], t: Translate): string {
  const names = members.map((m) => firstName(m.name) || t('chat.group.someone'));
  return t('chat.group.seenBy', { names: names.join(', ') });
}

// ─── The `receipt` frame ────────────────────────────────────────────────────────────────────────────────────

export interface ReceiptFrame {
  conversationId: string;
  metaSiteId: string;
  deliveredDate: string | null;
  readDate: string | null;
  readMessageId: string | null;
}

/** `{entityId: conversationId, entity: {conversationId, metaSiteId, deliveredDate?, readDate?, readMessageId?}}`. */
export function parseReceiptFrame(entityId: string | null | undefined, entity: unknown): ReceiptFrame | null {
  if (!isObject(entity)) return null;
  const conversationId = str(entity.conversationId) ?? str(entityId);
  const metaSiteId = str(entity.metaSiteId);
  if (!conversationId || !metaSiteId) return null;
  return { conversationId, metaSiteId, deliveredDate: str(entity.deliveredDate), readDate: str(entity.readDate), readMessageId: str(entity.readMessageId) };
}

/**
 * Fold one frame into the held receipts: a member's dates only move FORWARD (a late frame never undoes a newer read),
 * the read message id moves with its date, and a read that arrives is visible (the server sends it only then).
 * `known: false` when the receipts do not list that person (the caller may re-read, or ignore it). The same object
 * comes back when nothing moved.
 */
export function mergeReceiptFrame(receipts: GroupReceipts | null | undefined, frame: ReceiptFrame): { receipts: GroupReceipts | null; known: boolean } {
  if (!receipts) return { receipts: receipts ?? null, known: false };
  const index = receipts.members.findIndex((m) => m.metaSiteId === frame.metaSiteId);
  if (index < 0) return { receipts, known: false };
  const m = receipts.members[index];
  let next = m;
  if (frame.readDate && Number.isFinite(ms(frame.readDate)) && (!m.readDate || ms(frame.readDate) > ms(m.readDate))) {
    next = { ...next, readDate: frame.readDate, readMessageId: frame.readMessageId ?? next.readMessageId, readsHidden: false };
  }
  if (frame.deliveredDate && Number.isFinite(ms(frame.deliveredDate)) && (!m.deliveredDate || ms(frame.deliveredDate) > ms(m.deliveredDate))) {
    next = { ...next, deliveredDate: frame.deliveredDate };
  }
  if (next === m) return { receipts, known: true };
  const members = receipts.members.slice();
  members[index] = next;
  return { receipts: { ...receipts, members }, known: true };
}

// ─── Typing / Maestro thinking ──────────────────────────────────────────────────────────────────────────────

export type TypingKind = 'HUMAN' | 'MAESTRO';
export interface TypingFrame {
  conversationId: string;
  /** A person; for a MAESTRO, its OWNER. */
  metaSiteId: string;
  kind: TypingKind;
  name: string;
  typing: boolean;
  ttlSeconds: number;
}
export interface TypingEntry {
  metaSiteId: string;
  kind: TypingKind;
  name: string;
  /** Epoch ms after which the entry is dropped. */
  expiresAt: number;
}

/** A person's frame lives 6 s, a Maestro's 90 s (the server's ttl); one with no ttl takes the person's. */
const DEFAULT_TTL_SECONDS = 6;

export function parseTypingFrame(entityId: string | null | undefined, entity: unknown): TypingFrame | null {
  if (!isObject(entity)) return null;
  const conversationId = str(entity.conversationId) ?? str(entityId);
  const metaSiteId = str(entity.metaSiteId);
  if (!conversationId || !metaSiteId || typeof entity.typing !== 'boolean') return null;
  const ttl = typeof entity.ttlSeconds === 'number' && Number.isFinite(entity.ttlSeconds) && entity.ttlSeconds > 0 ? entity.ttlSeconds : DEFAULT_TTL_SECONDS;
  return {
    conversationId,
    metaSiteId,
    kind: entity.kind === 'MAESTRO' ? 'MAESTRO' : 'HUMAN',
    name: typeof entity.name === 'string' ? entity.name : '',
    typing: entity.typing,
    ttlSeconds: ttl,
  };
}

const sameTyper = (a: { kind: TypingKind; metaSiteId: string }, b: { kind: TypingKind; metaSiteId: string }) => a.kind === b.kind && a.metaSiteId === b.metaSiteId;

/**
 * One frame on a conversation's entries: `typing: true` (re)starts that typer's entry for `ttlSeconds`; false drops
 * it. A person and their own Maestro share a metasite and are told apart by kind.
 */
export function applyTypingFrame(entries: readonly TypingEntry[], frame: TypingFrame, now: number): TypingEntry[] {
  const rest = entries.filter((e) => !sameTyper(e, frame));
  if (!frame.typing) return rest;
  return [...rest, { metaSiteId: frame.metaSiteId, kind: frame.kind, name: frame.name, expiresAt: now + frame.ttlSeconds * 1000 }];
}

/**
 * A message arrived: its author is no longer typing — a person's row drops that person's entry, a Maestro's row
 * drops the entry of the Maestro whose owner wrote it (`agentOwnerMetaSiteId`, else the principal). The same array
 * when nothing was dropped.
 */
export function dropTypingForAuthor(entries: readonly TypingEntry[], author: MessageAuthor | null | undefined): readonly TypingEntry[] {
  if (!author || author.kind === 'SYSTEM') return entries;
  const id = author.kind === 'MAESTRO' ? author.agentOwnerMetaSiteId ?? author.principalMetaSiteId : author.principalMetaSiteId;
  const kept = entries.filter((e) => !(e.kind === author.kind && e.metaSiteId === id));
  return kept.length === entries.length ? entries : kept;
}

export function pruneTyping(entries: readonly TypingEntry[], now: number): readonly TypingEntry[] {
  const kept = entries.filter((e) => e.expiresAt > now);
  return kept.length === entries.length ? entries : kept;
}

/** When the next entry expires (for a re-render timer), or null with none. */
export function nextTypingExpiry(entries: readonly TypingEntry[]): number | null {
  return entries.length ? Math.min(...entries.map((e) => e.expiresAt)) : null;
}

function typerName(entry: TypingEntry, participants: readonly GroupParticipant[], t: Translate): string {
  const p = participants.find((x) => x.kind === 'HUMAN' && x.id === entry.metaSiteId);
  if (p) return shortName(p) || t('chat.group.someone');
  return firstName(entry.name) || t('chat.group.someone');
}

function maestroOwnerName(entry: TypingEntry, participants: readonly GroupParticipant[], t: Translate): string | null {
  const maestro = participants.find((x) => x.kind === 'MAESTRO' && x.ownerId === entry.metaSiteId);
  const owner = maestro?.ownerFirstName?.trim();
  if (owner) return owner;
  const human = participants.find((x) => x.kind === 'HUMAN' && x.id === entry.metaSiteId);
  if (human && human.displayName.trim()) return ownerShortName(human, t);
  return null;
}

/**
 * The line a conversation's typers draw — the chat header's subtitle, the chat list row's preview and the row at the
 * bottom of the chat: "Caro is typing…", "Caro and Alex are typing…", "3 people are typing…"; with no person typing,
 * "Caro's Maestro is thinking…" (else "Maestro is thinking…"). People win over a Maestro. The viewer's own entries
 * (`viewerId`) and expired ones are never drawn. Null when nobody is.
 */
export function typingLine(entries: readonly TypingEntry[], participants: readonly GroupParticipant[], viewerId: string | null | undefined, t: Translate, now: number): string | null {
  const live = entries.filter((e) => e.expiresAt > now && e.metaSiteId !== viewerId);
  const people = live.filter((e) => e.kind === 'HUMAN');
  if (people.length === 1) return t('chat.group.typing.one', { name: typerName(people[0], participants, t) });
  if (people.length === 2) return t('chat.group.typing.two', { name1: typerName(people[0], participants, t), name2: typerName(people[1], participants, t) });
  if (people.length > 2) return t('chat.group.typing.many', { count: people.length });
  const maestro = live.find((e) => e.kind === 'MAESTRO');
  if (!maestro) return null;
  const owner = maestroOwnerName(maestro, participants, t);
  return owner ? t('chat.group.typing.maestro', { name: owner }) : t('chat.group.typing.maestroGeneric');
}

// ─── Online / last seen ─────────────────────────────────────────────────────────────────────────────────────

export interface PresenceInfo {
  online: boolean;
  lastSeenDate: string | null;
  /** Their "last seen & online" is Nobody, the viewer's is, or a block: show nothing. */
  hidden: boolean;
}
export interface PresenceEntry extends PresenceInfo {
  /** Epoch ms this client received it — an "online" expires 150 s later without a refresh. */
  receivedAt: number;
}
export type PresenceMap = Readonly<Record<string, PresenceEntry>>;

/** How long a received "online" holds without a refresh (the server's own window is 120 s). */
export const PRESENCE_ONLINE_TTL_MS = 150_000;

/** A `members[].presence` — null when absent. */
export function parsePresence(raw: unknown): PresenceInfo | null {
  if (!isObject(raw)) return null;
  const hidden = raw.hidden === true;
  return { online: !hidden && raw.online === true, lastSeenDate: hidden ? null : str(raw.lastSeenDate), hidden };
}

/** `{entityId: metaSiteId, entity: {metaSiteId, online, lastSeenDate}}` — a frame reaches only who may see it. */
export function parsePresenceFrame(entityId: string | null | undefined, entity: unknown): { metaSiteId: string; presence: PresenceInfo } | null {
  if (!isObject(entity)) return null;
  const metaSiteId = str(entity.metaSiteId) ?? str(entityId);
  if (!metaSiteId) return null;
  return { metaSiteId, presence: { online: entity.online === true, lastSeenDate: str(entity.lastSeenDate), hidden: false } };
}

export function withPresence(map: PresenceMap, metaSiteId: string, info: PresenceInfo, now: number): PresenceMap {
  const prev = map[metaSiteId];
  if (prev && prev.online === info.online && prev.lastSeenDate === info.lastSeenDate && prev.hidden === info.hidden && now - prev.receivedAt < 1000) return map;
  return { ...map, [metaSiteId]: { ...info, receivedAt: now } };
}

/** Online now: a visible "online" received within the last 150 s. */
export function isOnline(entry: PresenceEntry | null | undefined, now: number): boolean {
  return !!entry && !entry.hidden && entry.online && now - entry.receivedAt < PRESENCE_ONLINE_TTL_MS;
}

const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** "last seen today at 14:02" / "last seen yesterday at 14:02" / "last seen 12 Oct at 14:02" (locale-formatted). */
export function lastSeenLine(iso: string | null | undefined, now: Date, t: Translate, locale?: string): string | null {
  const at = ms(iso ?? null);
  if (!Number.isFinite(at)) return null;
  const d = new Date(at);
  let time: string;
  let date: string;
  try {
    time = d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
    date = d.toLocaleDateString(locale, d.getFullYear() === now.getFullYear() ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    date = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  }
  if (sameDay(d, now)) return t('chat.group.presence.lastSeenToday', { time });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return t('chat.group.presence.lastSeenYesterday', { time });
  return t('chat.group.presence.lastSeenDate', { date, time });
}

/** One person's line (the group info's member list, a two-person header): "online", "last seen …", or nothing. */
export function memberPresenceLine(entry: PresenceEntry | null | undefined, now: number, t: Translate, locale?: string): string | null {
  if (!entry || entry.hidden) return null;
  if (isOnline(entry, now)) return t('chat.group.presence.online');
  return lastSeenLine(entry.lastSeenDate, new Date(now), t, locale);
}

/**
 * The chat header's subtitle when nobody is typing: with exactly one other person, theirs ("online" / "last seen …");
 * with more, "Caro, Alex online" (first names, three then "+N") when any are online. Null keeps the member count.
 */
export function headerPresenceLine(summary: GroupSummary, presence: PresenceMap, now: number, t: Translate, locale?: string): string | null {
  const people = summary.participants.filter((p) => p.kind === 'HUMAN' && p.id !== summary.viewerId);
  if (people.length === 0) return null;
  if (people.length === 1) return memberPresenceLine(presence[people[0].id], now, t, locale);
  const online = people.filter((p) => isOnline(presence[p.id], now)).map((p) => shortName(p)).filter(Boolean);
  if (online.length === 0) return null;
  const head = online.slice(0, 3).join(', ');
  return t('chat.group.presence.namesOnline', { names: online.length > 3 ? `${head}, +${online.length - 3}` : head });
}

// ─── Senders: the delivered ack and the typing hint ─────────────────────────────────────────────────────────

interface Timers {
  set: (fn: () => void, ms: number) => unknown;
  clear: (handle: unknown) => void;
}
const realTimers: Timers = {
  set: (fn, delay) => setTimeout(fn, delay),
  clear: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

interface Ack {
  id: string;
  at: number;
}
function isNewer(candidate: Ack, ref: Ack | undefined): boolean {
  if (!ref) return true;
  if (candidate.id === ref.id) return false;
  if (Number.isFinite(candidate.at) && Number.isFinite(ref.at)) return candidate.at > ref.at;
  return true;
}

/**
 * `viewer-state {deliveredThroughMessageId}`, coalesced per conversation: at most one call in flight, sent `delayMs`
 * after the first note, and only for a message newer than the last one acked (or pending). A failure is not retried
 * (it is a hint; the next message acks past it).
 */
export class DeliveredAcker {
  private readonly state = new Map<string, { acked?: Ack; pending?: Ack; timer?: unknown; inFlight: boolean }>();

  constructor(
    private readonly send: (conversationId: string, messageId: string) => Promise<unknown>,
    private readonly delayMs = 1500,
    private readonly timers: Timers = realTimers,
  ) {}

  note(conversationId: string, messageId: string, createdAt?: string | null): void {
    if (!conversationId || !messageId) return;
    let s = this.state.get(conversationId);
    if (!s) {
      s = { inFlight: false };
      this.state.set(conversationId, s);
    }
    const candidate = { id: messageId, at: ms(createdAt ?? null) };
    if (!isNewer(candidate, s.acked) || (s.pending && !isNewer(candidate, s.pending))) return;
    s.pending = candidate;
    if (!s.timer && !s.inFlight) s.timer = this.timers.set(() => this.flush(conversationId), this.delayMs);
  }

  private flush(conversationId: string): void {
    const s = this.state.get(conversationId);
    if (!s) return;
    s.timer = undefined;
    if (s.inFlight || !s.pending) return;
    const p = s.pending;
    s.pending = undefined;
    s.inFlight = true;
    const done = () => {
      s.inFlight = false;
      if (isNewer(p, s.acked)) s.acked = p;
      if (s.pending && !isNewer(s.pending, s.acked)) s.pending = undefined;
      if (s.pending && !s.timer) s.timer = this.timers.set(() => this.flush(conversationId), this.delayMs);
    };
    this.send(conversationId, p.id).then(done, done);
  }
}

/** A `typing: true` is re-sent at most every 3 s while typing; 5 s without a keystroke sends `typing: false`. */
export const TYPING_RESEND_MS = 3000;
export const TYPING_IDLE_MS = 5000;

/**
 * The composer's typing hint for ONE conversation: `onText` on every change of the text (non-empty → `true` at most
 * every 3 s, and an idle timer that sends `false`; empty → `false`), `stop` on send, leaving the chat or the app
 * going to the background. `false` is sent only after a `true`.
 */
export class TypingSender {
  private active = false;
  private lastTrueAt = Number.NEGATIVE_INFINITY;
  private idle: unknown;

  constructor(
    private readonly send: (typing: boolean) => void,
    private readonly now: () => number = Date.now,
    private readonly timers: Timers = realTimers,
  ) {}

  onText(text: string): void {
    if (!text.trim()) {
      this.stop();
      return;
    }
    const now = this.now();
    if (!this.active || now - this.lastTrueAt >= TYPING_RESEND_MS) {
      this.active = true;
      this.lastTrueAt = now;
      this.send(true);
    }
    if (this.idle !== undefined) this.timers.clear(this.idle);
    this.idle = this.timers.set(() => this.stop(), TYPING_IDLE_MS);
  }

  stop(): void {
    if (this.idle !== undefined) {
      this.timers.clear(this.idle);
      this.idle = undefined;
    }
    if (!this.active) return;
    this.active = false;
    this.lastTrueAt = Number.NEGATIVE_INFINITY;
    this.send(false);
  }
}

/** The heartbeat: FOREGROUND on open and every 30 s while in front, BACKGROUND on leaving. */
export const PRESENCE_HEARTBEAT_MS = 30_000;

// ─── The two privacy settings (Settings → Who can add me to groups' section) ────────────────────────────────

export type LastSeenSetting = 'EVERYONE' | 'NOBODY';
export interface GroupPrivacy {
  /** On by default; off, you send no read receipts and see nobody's (deliveries still show). */
  readReceipts: boolean;
  /** Everyone by default; Nobody, you share no last seen / online and see nobody's. */
  lastSeen: LastSeenSetting;
}
export const LAST_SEEN_OPTIONS: readonly { value: LastSeenSetting; labelKey: string }[] = [
  { value: 'EVERYONE', labelKey: 'settings.groupAdd.everyone' },
  { value: 'NOBODY', labelKey: 'settings.groupAdd.nobody' },
];

/** `settings` of a `my-settings` / `update-my-settings` answer; unset reads the defaults (on, Everyone). */
export function parseGroupPrivacy(data: unknown): GroupPrivacy {
  const settings = isObject(data) && isObject(data.settings) ? data.settings : {};
  return { readReceipts: settings.readReceipts !== false, lastSeen: settings.lastSeen === 'NOBODY' ? 'NOBODY' : 'EVERYONE' };
}
